import React, { useCallback, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ButtonBar, ScreenHeader } from '../../components';
import { useScrollToTop } from '../../hooks';

import { NewPOAContext } from './new-poa-composer';

interface NewPOASummaryProps {
  isContinue?: boolean;
}

export function NewPOASummary({ isContinue = false }: NewPOASummaryProps) {
  const { powerOfAttorney, updateCheckpoint } = useContext(NewPOAContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  useScrollToTop();
  
  const handleCancel = useCallback(() => {
    navigate('/home');
  }, [ navigate ]);

  const handleNext = useCallback(() => {
    updateCheckpoint(location.pathname);
    localStorage.setItem('poa:checkpoint', location.pathname);
    navigate(isContinue ? '../multiple-agents' : '../pdf');
  }, [ isContinue, location.pathname, navigate, updateCheckpoint ]);

  const handleEdit = useCallback((section: string) => {
    // Navigate to the appropriate edit screen
    console.log('Edit section:', section);
  }, []);

  const handleEditAgents = useCallback(() => handleEdit('agents'), [ handleEdit ]);
  const handleEditPowers = useCallback(() => handleEdit('powers'), [ handleEdit ]);
  const handleEditGifts = useCallback(() => handleEdit('gifts'), [ handleEdit ]);
  const handleEditOptions = useCallback(() => handleEdit('options'), [ handleEdit ]);
  
  return (
    <div className="new-poa-summary screen-container screen">
      <ScreenHeader
        title="Review Your Power of Attorney"
        description="Please review all the information below before finalizing your document"
      />
      
      <div className="summary-sections">
        {/* <div className="summary-section">
          <h3>Personal Information</h3>
          <div className="summary-content">
            <p><strong>Name:</strong> {powerOfAttorney.full_name}</p>
            <p><strong>Address:</strong> {powerOfAttorney.address}, {powerOfAttorney.city}, {powerOfAttorney.state} {powerOfAttorney.zip}</p>
            <p><strong>State of Residence:</strong> {powerOfAttorney.state_of_residence}</p>
          </div>
          <button onClick={() => handleEdit('personal')} className="edit-button">Edit</button>
        </div> */}

        <div className="summary-section">
          <h3>Agents</h3>
          <div className="summary-content">
            {powerOfAttorney.agents?.map((agent, index) => (
              <div key={index} className="agent-summary">
                <h4>Agent {index + 1}</h4>
                <p><strong>Name:</strong> {agent.name}</p>
                <p><strong>Address:</strong> {agent.address}, {agent.city}, {agent.state} {agent.zip}</p>
                <p><strong>Phone:</strong> {agent.phone}</p>
                {agent.email && <p><strong>Email:</strong> {agent.email}</p>}
              </div>
            ))}
            {powerOfAttorney.alternate_agents?.length > 0 && (
              <div className="alternate-agent-summary">
                <h4>Alternate Agent</h4>
                <p><strong>Name:</strong> {powerOfAttorney.alternate_agents[0].name}</p>
                <p><strong>Address:</strong> {powerOfAttorney.alternate_agents[0].address}, {powerOfAttorney.alternate_agents[0].city}, {powerOfAttorney.alternate_agents[0].state} {powerOfAttorney.alternate_agents[0].zip}</p>
                <p><strong>Phone:</strong> {powerOfAttorney.alternate_agents[0].phone}</p>
              </div>
            )}
          </div>
          <button onClick={handleEditAgents} className="edit-button">Edit</button>
        </div>

        <div className="summary-section">
          <h3>Powers Granted</h3>
          <div className="summary-content">
            <div className="powers-grid">
              {/* Financial Powers */}
              <div className="power-category">
                <h4>Financial Powers</h4>
                <div className="power-items">
                  <div className="power-item">
                    <span className="power-name">Banking Transactions</span>
                    <span className={`power-status ${powerOfAttorney.banking_transactions ? 'granted' : 'denied'}`}>
                      {powerOfAttorney.banking_transactions ? 'Granted' : 'Not Granted'}
                    </span>
                  </div>
                  <div className="power-item">
                    <span className="power-name">Securities</span>
                    <span className={`power-status ${powerOfAttorney.securities_transactions ? 'granted' : 'denied'}`}>
                      {powerOfAttorney.securities_transactions ? 'Granted' : 'Not Granted'}
                    </span>
                  </div>
                  <div className="power-item">
                    <span className="power-name">Insurance & Annuities</span>
                    <span className={`power-status ${powerOfAttorney.insurance_annuity ? 'granted' : 'denied'}`}>
                      {powerOfAttorney.insurance_annuity ? 'Granted' : 'Not Granted'}
                    </span>
                  </div>
                  <div className="power-item">
                    <span className="power-name">Retirement Plans</span>
                    <span className={`power-status ${powerOfAttorney.retirement_plan_transactions ? 'granted' : 'denied'}`}>
                      {powerOfAttorney.retirement_plan_transactions ? 'Granted' : 'Not Granted'}
                    </span>
                  </div>
                  <div className="power-item">
                    <span className="power-name">Tax Matters</span>
                    <span className={`power-status ${powerOfAttorney.tax_matters ? 'granted' : 'denied'}`}>
                      {powerOfAttorney.tax_matters ? 'Granted' : 'Not Granted'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Property Powers */}
              <div className="power-category">
                <h4>Property Powers</h4>
                <div className="power-items">
                  <div className="power-item">
                    <span className="power-name">Real Estate</span>
                    <span className={`power-status ${powerOfAttorney.real_estate_power ? 'granted' : 'denied'}`}>
                      {powerOfAttorney.real_estate_power || 'Not Granted'}
                    </span>
                  </div>
                  <div className="power-item">
                    <span className="power-name">Tangible Personal Property</span>
                    <span className={`power-status ${powerOfAttorney.tangible_personal_property ? 'granted' : 'denied'}`}>
                      {powerOfAttorney.tangible_personal_property ? 'Granted' : 'Not Granted'}
                    </span>
                  </div>
                  <div className="power-item">
                    <span className="power-name">Digital Assets</span>
                    <span className={`power-status ${powerOfAttorney.digital_assets ? 'granted' : 'denied'}`}>
                      {powerOfAttorney.digital_assets ? 'Granted' : 'Not Granted'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Business & Legal Powers */}
              <div className="power-category">
                <h4>Business & Legal Powers</h4>
                <div className="power-items">
                  <div className="power-item">
                    <span className="power-name">Business Operations</span>
                    <span className={`power-status ${powerOfAttorney.operating_business ? 'granted' : 'denied'}`}>
                      {powerOfAttorney.operating_business ? 'Granted' : 'Not Granted'}
                    </span>
                  </div>
                  <div className="power-item">
                    <span className="power-name">Estate & Trust Transactions</span>
                    <span className={`power-status ${powerOfAttorney.estate_trust_transactions ? 'granted' : 'denied'}`}>
                      {powerOfAttorney.estate_trust_transactions ? 'Granted' : 'Not Granted'}
                    </span>
                  </div>
                  <div className="power-item">
                    <span className="power-name">Legal Actions</span>
                    <span className={`power-status ${powerOfAttorney.legal_actions ? 'granted' : 'denied'}`}>
                      {powerOfAttorney.legal_actions ? 'Granted' : 'Not Granted'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Personal Care Powers */}
              <div className="power-category">
                <h4>Personal Care Powers</h4>
                <div className="power-items">
                  <div className="power-item">
                    <span className="power-name">Personal & Family Care</span>
                    <span className={`power-status ${powerOfAttorney.personal_family_care ? 'granted' : 'denied'}`}>
                      {powerOfAttorney.personal_family_care ? 'Granted' : 'Not Granted'}
                    </span>
                  </div>
                  <div className="power-item">
                    <span className="power-name">Pet Care</span>
                    <span className={`power-status ${powerOfAttorney.pet_care ? 'granted' : 'denied'}`}>
                      {powerOfAttorney.pet_care ? 'Granted' : 'Not Granted'}
                    </span>
                  </div>
                  <div className="power-item">
                    <span className="power-name">Government Benefits</span>
                    <span className={`power-status ${powerOfAttorney.government_benefits ? 'granted' : 'denied'}`}>
                      {powerOfAttorney.government_benefits ? 'Granted' : 'Not Granted'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button onClick={handleEditPowers} className="edit-button">Edit</button>
        </div>

        {powerOfAttorney.allow_gifts && (
          <div className="summary-section">
            <h3>Gift Authorization</h3>
            <div className="summary-content">
              <p><strong>Allow Gifts:</strong> Yes</p>
              <p><strong>Gifts to Agent:</strong> {powerOfAttorney.gifts_to_agent ? 'Yes' : 'No'}</p>
              {powerOfAttorney.max_gift_amount_per_year && (
                <p><strong>Max Amount per Year:</strong> ${powerOfAttorney.max_gift_amount_per_year}</p>
              )}
              <p><strong>Gifts to Others:</strong> {powerOfAttorney.gifts_to_others}</p>
            </div>
            <button onClick={handleEditGifts} className="edit-button">Edit</button>
          </div>
        )}

        <div className="summary-section">
          <h3>Additional Options</h3>
          <div className="summary-content">
            <div className="options-grid">
              {/* Agent Management */}
              <div className="option-category">
                <h4>Agent Management</h4>
                <div className="option-items">
                  <div className="option-item">
                    <span className="option-name">Allow Delegating Tasks</span>
                    <span className={`option-status ${powerOfAttorney.allow_delegating_tasks ? 'enabled' : 'disabled'}`}>
                      {powerOfAttorney.allow_delegating_tasks ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="option-item">
                    <span className="option-name">Allow Personal Benefit</span>
                    <span className={`option-status ${powerOfAttorney.allow_personal_benefit ? 'enabled' : 'disabled'}`}>
                      {powerOfAttorney.allow_personal_benefit ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="option-item">
                    <span className="option-name">Pay Agent</span>
                    <span className="option-value">{powerOfAttorney.pay_agent || 'Not specified'}</span>
                  </div>
                </div>
              </div>

              {/* Financial Management */}
              <div className="option-category">
                <h4>Financial Management</h4>
                <div className="option-items">
                  <div className="option-item">
                    <span className="option-name">Commingle Funds</span>
                    <span className={`option-status ${powerOfAttorney.commingle_funds ? 'enabled' : 'disabled'}`}>
                      {powerOfAttorney.commingle_funds ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Oversight & Reporting */}
              <div className="option-category">
                <h4>Oversight & Reporting</h4>
                <div className="option-items">
                  <div className="option-item">
                    <span className="option-name">Require Period Reports</span>
                    <span className={`option-status ${powerOfAttorney.require_period_reports ? 'enabled' : 'disabled'}`}>
                      {powerOfAttorney.require_period_reports ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Legal Considerations */}
              <div className="option-category">
                <h4>Legal Considerations</h4>
                <div className="option-items">
                  <div className="option-item">
                    <span className="option-name">Nominate as Conservator</span>
                    <span className={`option-status ${powerOfAttorney.nominate_as_conservator ? 'enabled' : 'disabled'}`}>
                      {powerOfAttorney.nominate_as_conservator ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button onClick={handleEditOptions} className="edit-button">Edit</button>
        </div>
      </div>

      <ButtonBar 
        handlePrimary={handleNext}
        handleSecondary={handleCancel}
        primaryText={isContinue ? 'Continue' : 'Generate Document'}
        secondaryText="Cancel"
      />
    </div>
  );
}
